
import React, { useState, useEffect } from 'react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { LogOut, User, MessageSquare, Briefcase } from 'lucide-react';
import { useNavigate, Outlet } from 'react-router-dom';
import { api } from '../utils/apiClient';
import { SiteSettings } from '../types';

const CustomerLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { customer, logout } = useCustomerAuth();
  const navigate = useNavigate();
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await api.from('site_settings').select('*').single();
      if (data) setSiteSettings(data);
    };
    fetchSettings();
  }, []);

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* Customer Top Navigation */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                {siteSettings?.logoUrl ? (
                  <img src={siteSettings.logoUrl} alt={siteSettings.siteName} className="h-8 w-auto mr-2" />
                ) : (
                  <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl mr-2">
                    {siteSettings?.siteName?.charAt(0) || 'E'}
                  </div>
                )}
                <span className="font-bold text-gray-900 text-lg hidden sm:block">
                  {siteSettings?.siteName ? `${siteSettings.siteName} Portal` : 'Travel Portal'}
                </span>
              </div>
            </div>
            
            {customer && (
                <div className="flex items-center space-x-4">
                    <div className="flex items-center text-sm font-medium text-gray-700">
                        <img 
                            src={customer.avatar || `https://ui-avatars.com/api/?name=${customer?.firstName || 'User'}+${customer?.lastName || ''}`} 
                            alt="Profile" 
                            className="h-8 w-8 rounded-full border border-gray-200 mr-2"
                        />
                        <span className="hidden sm:inline">Hello, {customer?.firstName || 'User'}</span>
                    </div>
                    <div className="h-6 w-px bg-gray-300 mx-2"></div>
                    <button 
                        onClick={handleLogout}
                        className="text-gray-500 hover:text-red-600 transition-colors p-2 rounded-full hover:bg-gray-100"
                        title="Logout"
                    >
                        <LogOut className="h-5 w-5" />
                    </button>
                </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {children || <Outlet />}
      </main>
      
      <footer className="bg-white border-t border-gray-200 mt-auto py-6">
          <div className="max-w-7xl mx-auto px-4 text-center text-gray-500 text-sm">
              <p>&copy; {new Date().getFullYear()} {siteSettings?.siteName || 'EsiringetCLOUD'}. All rights reserved.</p>
              <p className="mt-1">
                Need help? Contact support at{' '}
                <a href={`tel:${siteSettings?.supportPhone}`} className="text-gray-900 font-bold hover:text-blue-600 transition-colors">
                    {siteSettings?.supportPhone || '+255 123 456 789'}
                </a>
                {' '}or{' '}
                <a href={`mailto:${siteSettings?.supportEmail}`} className="text-gray-900 font-bold hover:text-blue-600 transition-colors">
                    {siteSettings?.supportEmail}
                </a>
              </p>
          </div>
      </footer>
    </div>
  );
};

export default CustomerLayout;


